import React, { useContext, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table } from 'reactstrap';
import { ContaReceberContext } from '../../context/ContaReceberContext';
import { ContaReceberType } from '../../context/hooks/useContaReceber';
import { useToast } from '../../context/ToastContext';

// Mesmo limite do backend (Portal.Formatura.Api/ContaReceberController) — cada PDF é gerado
// na hora, então um lote grande demais fica lento. Acima disso o download é quebrado em
// vários arquivos.
const MAX_BOLETOS_POR_DOWNLOAD = 100;

interface BoletoParcelasModalProps {
    isOpen: boolean;
    toggle: () => void;
    vendaId: number | null;
    // Só as parcelas com boleto emitido (a entrada, quando existe, não entra aqui).
    parcelas: ContaReceberType[];
}

const moeda = (valor: number) =>
    valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const data = (vencimento: string) =>
    vencimento ? vencimento.split('-').reverse().join('/') : '-';

// Visão simples e resumida das parcelas de uma venda, pensada só pra decidir o que baixar:
// quantas existem, quantas já foram pagas, e a lista do que ainda está em aberto.
export const BoletoParcelasModal = ({ isOpen, toggle, vendaId, parcelas }: BoletoParcelasModalProps) => {
    const { baixarBoletos } = useContext(ContaReceberContext);
    const showToast = useToast();
    const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
    const [baixando, setBaixando] = useState(false);

    useEffect(() => {
        if (isOpen) setSelecionadas(new Set());
    }, [isOpen, vendaId]);

    const pendentes = parcelas.filter(p => p.status !== 1);
    const pagas = parcelas.length - pendentes.length;
    const todasPendentesSelecionadas = pendentes.length > 0 && pendentes.every(p => selecionadas.has(p.id));

    const toggleParcela = (id: number) => {
        setSelecionadas(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleTodasPendentes = () => {
        setSelecionadas(todasPendentesSelecionadas ? new Set() : new Set(pendentes.map(p => p.id)));
    };

    const handleBaixar = async (formato: 'pdf' | 'zip') => {
        if (selecionadas.size === 0) return;

        const ids = Array.from(selecionadas);
        const extensao = formato === 'zip' ? 'zip' : 'pdf';
        const lotes: number[][] = [];
        for (let i = 0; i < ids.length; i += MAX_BOLETOS_POR_DOWNLOAD) {
            lotes.push(ids.slice(i, i + MAX_BOLETOS_POR_DOWNLOAD));
        }

        if (lotes.length > 1) {
            showToast('warning', `${ids.length} boletos serão entregues em ${lotes.length} arquivos.`);
        }

        setBaixando(true);
        try {
            for (let i = 0; i < lotes.length; i++) {
                const nome = lotes.length > 1
                    ? `boletos_venda_${vendaId}_parte${i + 1}de${lotes.length}.${extensao}`
                    : `boletos_venda_${vendaId}.${extensao}`;
                const ok = await baixarBoletos(lotes[i], formato, nome);
                if (!ok) break;
            }
        } finally {
            setBaixando(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>Boletos da venda #{vendaId}</ModalHeader>
            <ModalBody>
                <div className="d-flex justify-content-around text-center mb-3">
                    <div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{parcelas.length}</div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#2dce89' }}>{pagas}</div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>Pagas</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#fb6340' }}>{pendentes.length}</div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>Pendentes</div>
                    </div>
                </div>

                {pendentes.length === 0 ? (
                    <div className="text-muted text-center py-3">Não há parcelas em aberto nesta venda.</div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong style={{ fontSize: '0.85rem' }}>Parcelas em aberto</strong>
                            {/* Checkbox em vez de botão-link: o link "Selecionar todas" ficava com
                                um resíduo visual de foco depois de clicado, parecendo travado — um
                                checkbox tem estado marcado/desmarcado sem ambiguidade. */}
                            <label className="d-flex align-items-center mb-0" style={{ fontSize: '0.8rem', cursor: 'pointer', gap: 6 }}>
                                <input
                                    type="checkbox"
                                    checked={todasPendentesSelecionadas}
                                    onChange={toggleTodasPendentes}
                                />
                                Selecionar todas
                            </label>
                        </div>
                        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                            <Table size="sm" borderless className="mb-0">
                                <tbody>
                                    {pendentes.map(parcela => (
                                        <tr key={parcela.id}>
                                            <td style={{ width: 28 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selecionadas.has(parcela.id)}
                                                    onChange={() => toggleParcela(parcela.id)}
                                                />
                                            </td>
                                            <td>Parcela {parcela.numeroParcela}/{parcela.totalParcelas}</td>
                                            <td>Venc. {data(parcela.vencimento)}</td>
                                            <td className="text-right">{moeda(parcela.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button onClick={toggle}>Cancelar</Button>
                <Button
                    disabled={selecionadas.size === 0 || baixando}
                    style={{ color: '#FFF', backgroundColor: '#5e72e4' }}
                    onClick={() => handleBaixar('pdf')}
                >
                    Baixar {selecionadas.size > 0 ? `(${selecionadas.size}) ` : ''}— PDF único
                </Button>
                <Button
                    disabled={selecionadas.size === 0 || baixando}
                    style={{ color: '#FFF', backgroundColor: '#5e72e4' }}
                    onClick={() => handleBaixar('zip')}
                >
                    Baixar — ZIP
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default BoletoParcelasModal;
